import { useTranslation } from '@pancakeswap/localization'
import { Box, Card, EmptyState, Heading, LoadingState, Text } from '@pancakeswap/uikit'
import { useMemo } from 'react'
import useSWR from 'swr'
import type { InfoTransaction, Section } from 'server/info/types'
import { DexId, VersionParam } from 'state/infoHub/hooks'
import TransactionTable from 'views/Info/components/InfoTables/TransactionsTable'
import { DataStateBar } from './DataStateBar'
import { toLegacyTransactions } from './PoolDetailView'

const fetcher = (u: string) => fetch(u).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))

/**
 * Latest swaps, adds and removes of the DEX: CADINUdex from our subgraphs, PancakeSwap (swaps of the CADINU/CBON pools) from the shared chain
 * trades feed; the section says what it covers. With no rows, the server's plain sentence (`meta.emptyText`) is the message.
 */
export const Transactions: React.FC<{ dex: DexId; version: VersionParam }> = ({ dex, version }) => {
  const { t } = useTranslation()
  const { data, error, isLoading } = useSWR<Section<InfoTransaction[]>>(`/api/info/transactions?dex=${dex}&version=${version}`, fetcher, {
    refreshInterval: 60_000,
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })
  const rows = useMemo(() => {
    return toLegacyTransactions(data?.data ?? [])
  }, [data])
  const meta = data?.meta
  // an empty section that says why in plain words needs no status bar on top ("No trades in the last 3 hours on these pools")
  const plainEmpty = rows.length === 0 && !!meta?.emptyText && meta.state !== 'partial'
  return (
    <Box mt="32px" data-testid="ih-transactions">
      <Heading scale="md" as="h3" mb="12px">
        {t('Transactions')}
      </Heading>
      {meta && !plainEmpty && (meta.state !== 'ok' || meta.warnings.length > 0) && (
        <Box mb="12px">
          <DataStateBar meta={meta} compact />
        </Box>
      )}
      <Card>
        {isLoading && !data ? (
          <LoadingState rows={5} label={t('Loading transactions')} />
        ) : error && !data ? (
          <EmptyState title={t('Transactions could not be loaded')} description={error.message} py="24px" />
        ) : rows.length === 0 ? (
          <EmptyState title={meta?.emptyText ?? t('No transactions in this scope')} description={meta?.coverage} py="24px" />
        ) : (
          <TransactionTable transactions={rows} />
        )}
      </Card>
      {data?.meta?.state === 'ok' && (
        <Text fontSize="12px" color="textSubtle" mt="6px">
          {data.meta.coverage}
        </Text>
      )}
    </Box>
  )
}

export default Transactions
